/**
 * sl-kpg5.21: AI Registry API Hooks
 *
 * React hooks that fetch model and provider data from the Backend Registry REST API.
 * Used by all workspace-level consumers for dynamic model/provider data.
 *
 * Backend endpoints (ai-registry.controller.ts):
 * - GET /api/workspace/:wsId/ai/registry/models
 * - GET /api/workspace/:wsId/ai/registry/providers
 *
 * sl-oe5x: Wrapped in `useSWRImmutable` so the AI panel's provider-slot,
 * model-slot, and `clear-if-invalid` effect — which all hit
 * `/ai/registry/models` with the same params — collapse to ONE network
 * request per unique URL. Removes the 3× models + 2× providers fan-out
 * flow tracing surfaced (incident 2026-04-19).
 */
import { useCallback, useMemo } from 'react';
import useSWRImmutable from 'swr/immutable';

import { defaultWorkspaceApiClient } from '../../modules/cloud';
import type { AIModality, AIProvider, ModelOption } from './types';

// =============================================================================
// Backend Response Types (mirror ai-registry.interface.ts)
// =============================================================================

export interface RegistryModelPricing {
  inputPriceMicroUsd: number | null;
  outputPriceMicroUsd: number | null;
  imagePriceMicroUsd: number | null;
  audioPricePerMinMicroUsd: number | null;
  // sl-67kc.1.1: Discounted price for batch/dynamic-batching STT submissions.
  batchAudioPricePerMinMicroUsd: number | null;
}

export interface RegistryModelTemperature {
  temperatureMin: number | null;
  temperatureMax: number | null;
  temperatureDefault: number | null;
}

export interface RegistryModel {
  id: string;
  providerId: string;
  name: string;
  capabilities: string[];
  tier: string;
  status: string;
  supportsReasoning: boolean;
  supportsAdaptiveThinking: boolean;
  usesThinkingLevel: boolean;
  supportsSamplingControls: boolean;
  supportsStreaming: boolean;
  contextWindow: number | null;
  maxOutputTokens: number | null;
  pricing: RegistryModelPricing;
  temperature: RegistryModelTemperature;
  enabled: boolean;
  isOverridden: boolean;
}

export interface RegistryProvider {
  id: string;
  name: string;
  type: string;
  apiBaseUrl: string | null;
  authType: string;
  isBuiltIn: boolean;
  models: RegistryModel[];
}

// =============================================================================
// Hooks
// =============================================================================

/**
 * Options for {@link useRegistryModels}.
 *
 * sl-rdit: `availableOnly` switches the hook from the full platform model
 * list to a BYOK-aware set filtered by the user's actually usable
 * providers (User Personal > Workspace BYOK > Managed > Server). Admin
 * panels that configure tier limits or provider availability must leave
 * this false; user-facing selectors (workflow builder AI step) should
 * enable it so users only see models they can actually run.
 */
export interface UseRegistryModelsOptions {
  capability?: string;
  availableOnly?: boolean;
}

function modelsUrl(
  workspaceId: string,
  opts: UseRegistryModelsOptions
): string {
  const query = new URLSearchParams();
  if (opts.capability) query.set('capability', opts.capability);
  if (opts.availableOnly) query.set('availableOnly', 'true');
  const qs = query.toString();
  return `/api/workspace/${workspaceId}/ai/registry/models${qs ? `?${qs}` : ''}`;
}

type WorkspaceApiSWRKey = readonly [workspaceId: string, url: string];

async function fetchWorkspaceJson<T>(key: WorkspaceApiSWRKey): Promise<T> {
  const [workspaceId, url] = key;
  return defaultWorkspaceApiClient.requestJson<T>({
    workspaceId,
    path: url,
    category: 'ai-bootstrap',
    key: `GET:${url}`,
    mode: 'background',
    priority: 'visible',
  });
}

/**
 * Fetch models from Registry API, optionally filtered by capability and
 * BYOK availability. Returns raw backend data + derived helper maps.
 *
 * Deduplicated per URL via SWR: three consumers calling with the same
 * `{capability, availableOnly}` share one network request and one cache
 * entry for the session.
 */
export function useRegistryModels(
  workspaceId: string,
  options: UseRegistryModelsOptions | string = {}
) {
  // Back-compat: legacy signature accepted `capability?: string` as the
  // second positional arg. Normalize both shapes into an options object.
  const normalized: UseRegistryModelsOptions =
    typeof options === 'string' ? { capability: options } : options;
  const { capability, availableOnly = false } = normalized;

  const key = workspaceId
    ? modelsUrl(workspaceId, { capability, availableOnly })
    : null;
  const swrKey = workspaceId && key ? ([workspaceId, key] as const) : null;
  const { data, error, mutate } = useSWRImmutable<{ models: RegistryModel[] }>(
    swrKey,
    fetchWorkspaceJson,
    { suspense: false }
  );

  const models = useMemo(() => data?.models ?? [], [data?.models]);
  const isLoading = !!swrKey && !data && !error;
  const refetch = useCallback(async () => {
    await mutate();
  }, [mutate]);

  /**
   * Models grouped by provider ID → ModelOption[]
   * (replaces static MODELS_BY_PROVIDER)
   */
  const modelsByProvider = useMemo(() => {
    const result: Record<string, ModelOption[]> = {};
    for (const model of models) {
      if (model.status === 'deprecated' || !model.enabled) continue;
      if (!result[model.providerId]) result[model.providerId] = [];
      result[model.providerId].push({
        id: model.id,
        name: model.name,
        provider: model.providerId as AIProvider,
        providerName: undefined, // populated by caller with provider data
        supportsReasoning: model.supportsReasoning,
        supportsAdaptiveThinking: model.supportsAdaptiveThinking,
        usesThinkingLevel: model.usesThinkingLevel,
        supportsSamplingControls: model.supportsSamplingControls,
      });
    }
    return result as Record<AIProvider, ModelOption[]>;
  }, [models]);

  /**
   * Modality/capability → model IDs
   * (replaces static MODALITY_MODELS)
   */
  const modalityModelIds = useMemo(() => {
    const result: Record<string, string[]> = {};
    for (const model of models) {
      if (model.status === 'deprecated' || !model.enabled) continue;
      for (const cap of model.capabilities) {
        if (!result[cap]) result[cap] = [];
        result[cap].push(model.id);
      }
    }
    return result as Record<AIModality, string[]>;
  }, [models]);

  /**
   * Set of model IDs that support reasoning (for quick lookup)
   */
  const reasoningModelIds = useMemo(
    () => new Set(models.filter(m => m.supportsReasoning).map(m => m.id)),
    [models]
  );

  return {
    models,
    isLoading,
    refetch,
    modelsByProvider,
    modalityModelIds,
    reasoningModelIds,
  };
}

// =============================================================================
// Resolved AI Defaults (workspace → platform fallback)
// =============================================================================

/**
 * Resolved default model reference for a single capability.
 * Returned by GET /api/workspace/:wsId/ai-defaults/resolved.
 */
export interface ResolvedAIDefault {
  modelId: string;
  providerId: string;
}

/**
 * Full resolved-defaults payload keyed by capability.
 * A value of `null` means neither workspace nor platform has a default.
 */
export interface ResolvedAIDefaults {
  chat: ResolvedAIDefault | null;
  image: ResolvedAIDefault | null;
  stt: ResolvedAIDefault | null;
  webSearch: ResolvedAIDefault | null;
  embedding: ResolvedAIDefault | null;
  vision: ResolvedAIDefault | null;
}

const EMPTY_RESOLVED_DEFAULTS: ResolvedAIDefaults = {
  chat: null,
  image: null,
  stt: null,
  webSearch: null,
  embedding: null,
  vision: null,
};

/**
 * Fetch resolved AI defaults (workspace default → platform default fallback)
 * for all capabilities from the backend.
 *
 * Backend endpoint: GET /api/workspace/:wsId/ai-defaults/resolved
 */
export function useResolvedAIDefaults(workspaceId: string) {
  const url = workspaceId
    ? `/api/workspace/${workspaceId}/ai-defaults/resolved`
    : null;
  const swrKey = workspaceId && url ? ([workspaceId, url] as const) : null;
  const { data, error, mutate } = useSWRImmutable<Partial<ResolvedAIDefaults>>(
    swrKey,
    fetchWorkspaceJson,
    { suspense: false }
  );
  const defaults: ResolvedAIDefaults = data
    ? { ...EMPTY_RESOLVED_DEFAULTS, ...data }
    : EMPTY_RESOLVED_DEFAULTS;
  const isLoading = !!swrKey && !data && !error;
  const refetch = useCallback(async () => {
    await mutate();
  }, [mutate]);
  return { defaults, isLoading, refetch };
}

/**
 * Fetch all providers with their models from Registry API.
 *
 * Deduplicated via SWR — multiple panels or widgets calling this with the
 * same workspaceId share a single `/ai/registry/providers` request.
 */
export function useRegistryProviders(workspaceId: string) {
  const url = workspaceId
    ? `/api/workspace/${workspaceId}/ai/registry/providers`
    : null;
  const swrKey = workspaceId && url ? ([workspaceId, url] as const) : null;
  const { data, error, mutate } = useSWRImmutable<{
    providers: RegistryProvider[];
  }>(swrKey, fetchWorkspaceJson, { suspense: false });

  const providers = useMemo(() => data?.providers ?? [], [data?.providers]);
  const isLoading = !!swrKey && !data && !error;
  const refetch = useCallback(async () => {
    await mutate();
  }, [mutate]);

  /** Quick lookup: provider ID → provider name */
  const providerNameMap = useMemo(
    () => new Map(providers.map(p => [p.id, p.name])),
    [providers]
  );

  return { providers, isLoading, refetch, providerNameMap };
}
